/**
 * Spanish (es-US) pages: one markdown file per page in src/content/es, in the
 * blog/landing markdown format plus three keys:
 *
 *   path:          /es/...        the page's URL
 *   translationOf: /english/path  the English page it corresponds to (hreflang pair)
 *   status:        draft | reviewed
 *
 * A page goes live for search only once a Spanish-speaking team member has
 * reviewed it and set `status: reviewed`. Until then it is built with
 * `noindex`, kept out of the sitemap and hreflang, and not linked from the
 * language switch. Google treats unreviewed machine translation as low-quality
 * content, so this gate protects the English site too.
 *
 * Pure (no Vite, no DOM): the app and scripts/prerender.mjs render from it.
 */
import { parsePost, renderMarkdown, escapeHtml, SITE_ORIGIN, type BlogPostData } from './blogFormat';

export interface SpanishPageData extends BlogPostData {
  path: string;
  translationOf: string;
  status: 'draft' | 'reviewed';
  h1?: string;
  subtitle?: string;
}

export function parseSpanish(source: string, fileName = '?'): SpanishPageData {
  const p = parsePost(source, fileName) as SpanishPageData;
  if (!/^\/es(\/[a-z0-9-]+)*$/.test(p.path || '')) throw new Error(`es ${fileName}: path must be /es or /es/<lowercase-slug>/..., got "${p.path}"`);
  if (!p.translationOf?.startsWith('/')) throw new Error(`es ${fileName}: translationOf must be an English path`);
  if (p.status !== 'draft' && p.status !== 'reviewed') throw new Error(`es ${fileName}: status must be draft or reviewed`);
  return p;
}

export const isLive = (p: SpanishPageData) => p.status === 'reviewed';
export const spanishUrl = (p: SpanishPageData) => `${SITE_ORIGIN}${p.path}`;

/** hreflang pair for a reviewed page, used on both the English and Spanish page. */
export const hreflangLinks = (p: SpanishPageData) => {
  const en = `${SITE_ORIGIN}${p.translationOf === '/' ? '/' : p.translationOf}`;
  return [
    { hreflang: 'en-US', href: en },
    { hreflang: 'es-US', href: spanishUrl(p) },
    { hreflang: 'x-default', href: en },
  ];
};

export function spanishJsonLd(p: SpanishPageData) {
  const url = spanishUrl(p);
  const graph: Record<string, unknown>[] = [
    {
      '@type': 'WebPage',
      name: p.h1 || p.title,
      description: p.description,
      url,
      inLanguage: 'es-US',
      isPartOf: { '@id': `${SITE_ORIGIN}/#website` },
      publisher: { '@id': `${SITE_ORIGIN}/#organization` },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Inicio', item: `${SITE_ORIGIN}/es` },
        ...(p.path === '/es' ? [] : [{ '@type': 'ListItem', position: 2, name: p.h1 || p.title, item: url }]),
      ],
    },
  ];
  if (p.faq.length) {
    graph.push({
      '@type': 'FAQPage',
      inLanguage: 'es-US',
      mainEntity: p.faq.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
    });
  }
  return { '@context': 'https://schema.org', '@graph': graph };
}

/** Main content as static HTML, shared by React and the prerenderer. */
export function renderSpanish(p: SpanishPageData): string {
  const takeaways = p.takeaways.length
    ? `<aside class="takeaways" aria-label="De un vistazo"><h2>De un vistazo</h2><ul>${p.takeaways.map((t) => `<li>${escapeHtml(t)}</li>`).join('')}</ul></aside>`
    : '';
  const faq = p.faq.length
    ? `<section class="faq" aria-labelledby="faq"><h2 id="faq">Preguntas frecuentes</h2>${p.faq
        .map((f) => `<h3>${escapeHtml(f.q)}</h3><p>${escapeHtml(f.a)}</p>`)
        .join('')}</section>`
    : '';
  return [
    `<nav class="crumbs" aria-label="Ruta"><a href="/es">Inicio</a>${p.path === '/es' ? '' : ` / <span>${escapeHtml(p.h1 || p.title)}</span>`} · <a href="${p.translationOf}" hreflang="en" lang="en">English</a></nav>`,
    `<h1>${escapeHtml(p.h1 || p.title)}</h1>`,
    p.subtitle ? `<p class="subtitle">${escapeHtml(p.subtitle)}</p>` : '',
    takeaways,
    renderMarkdown(p.body),
    faq,
  ].filter(Boolean).join('\n');
}
